import { Badge } from '@/components/ui/badge';
import type { ReactElement, ReactNode } from 'react';
import {
    CredentialTypeDisplayName,
    CredentialTypeRegistry,
    CredentialType,
    ThirdPartySystemId,
    ThirdPartySystemIdToName,
} from '@pillar-portal/api-types';

type CredentialCardDisplayData = {
    account_id: string;
    system_id: ThirdPartySystemId;
    credential_type: CredentialType;
    is_active: boolean;
    applies_to_children: boolean;
    public_fields: Record<string, unknown>;
};

type CredentialCardViewProps = {
    credential: CredentialCardDisplayData;
    showPublicFields: boolean;
    actions?: ReactNode;
};

type PublicFieldEntry = {
    key: string;
    value: string;
};

function getSystemDisplayName(systemId: ThirdPartySystemId): string {
    const systemName: string | undefined = ThirdPartySystemIdToName[systemId];
    if (systemName === undefined) {
        return `System ${systemId}`;
    }
    return systemName
        .toLowerCase()
        .split('_')
        .map((segment: string) => {
            if (segment.length <= 2) {
                return segment.toUpperCase();
            }
            return segment.charAt(0).toUpperCase() + segment.slice(1);
        })
        .join(' ');
}

function getCredentialPublicFieldEntries(
    credential: CredentialCardDisplayData
): ReadonlyArray<PublicFieldEntry> {
    const credentialTypeDefinition = CredentialTypeRegistry[credential.system_id];
    if (credentialTypeDefinition === undefined) {
        return [];
    }
    const publicFieldEntries: PublicFieldEntry[] = [];
    for (const fieldName of credentialTypeDefinition.publicFields) {
        const rawFieldValue: unknown = credential.public_fields[fieldName];
        if (rawFieldValue !== null && rawFieldValue !== undefined) {
            publicFieldEntries.push({ key: fieldName, value: String(rawFieldValue) });
        }
    }
    return publicFieldEntries;
}

export function CredentialCardView({
    credential,
    showPublicFields,
    actions,
}: CredentialCardViewProps): ReactElement {
    return (
        <div className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
            <div className="flex justify-between items-start gap-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <h4 className="font-medium">{credential.account_id}</h4>
                        <Badge variant="outline">{getSystemDisplayName(credential.system_id)}</Badge>
                        <Badge variant="secondary">
                            {CredentialTypeDisplayName[credential.credential_type]}
                        </Badge>
                        {!credential.is_active && <Badge variant="destructive">Inactive</Badge>}
                        {credential.applies_to_children && (
                            <Badge variant="secondary">Inherited by Children</Badge>
                        )}
                    </div>
                    {showPublicFields && (
                        <div className="text-sm text-muted-foreground space-y-1">
                            {getCredentialPublicFieldEntries(credential).map((entry) => (
                                <div key={entry.key}>
                                    <span className="font-medium">{entry.key}</span>: {entry.value}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                {actions}
            </div>
        </div>
    );
}
